import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Play, X, UserCheck, ArrowUp, ChevronDown } from "lucide-react";
import { Performer } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ReplacementInfo } from "@/components/replacement-info";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SelectedForStageTableProps {
  performers: Performer[];
  jamNightId?: number;
  disabled?: boolean;
  forceMobileView?: boolean;
}

export default function SelectedForStageTable({
  performers,
  jamNightId,
  disabled = false,
  forceMobileView = false,
}: SelectedForStageTableProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Filter to only show performers selected for stage and sort with replacements first
  const selectedPerformers = performers
    .filter((performer) => performer.status === 'selected')
    .sort((a, b) => {
      // First, sort by replacement status - performers replacing someone come first
      if (a.replacingPerformerId && !b.replacingPerformerId) return -1;
      if (!a.replacingPerformerId && b.replacingPerformerId) return 1;
      
      // Then sort alphabetically by name
      return a.name.localeCompare(b.name);
    });

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/performers/${id}`,
        updates,
      );
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  // Handle instrument change - switch what they're currently selected to play
  const handleInstrumentChange = (performerId: number, instrument: string) => {
    // Set single instrument as current instrument
    const newCurrentInstruments = [instrument];
    
    // Update performer's current instrument (what they're selected to play)
    updatePerformerMutation.mutate({
      id: performerId,
      updates: {
        currentInstruments: newCurrentInstruments,
      },
    });
  };

  const movePerformerToStage = (performer: Performer) => {
    // If this performer is replacing someone, remove the replaced performer from stage first
    if (performer.replacingPerformerId) {
      const replacedPerformer = performers.find(
        (p) => p.id === performer.replacingPerformerId,
      );
      if (replacedPerformer && replacedPerformer.status === 'on_stage') {
        // Calculate elapsed time based on stageStartTime
        let elapsedMinutes = 0;
        if (replacedPerformer.stageStartTime) {
          const startTime = new Date(replacedPerformer.stageStartTime);
          const elapsed = Math.floor(
            (Date.now() - startTime.getTime()) / 1000 / 60,
          );
          elapsedMinutes = Math.max(0, elapsed);
        }

        updatePerformerMutation.mutate({
          id: performer.replacingPerformerId,
          updates: {
            status: 'participant',
            currentInstruments: [],
            timeOnStage: (replacedPerformer.timeOnStage || 0) + elapsedMinutes,
            played: true,
            replacementName: null,
          },
        });
      }
    }

    // Move performer to stage
    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'on_stage',
        played: true,
        stageStartTime: null,
        replacingPerformerId: null,
      },
    });
  };

  const handleMoveToStage = (performer: Performer) => {
    movePerformerToStage(performer);
  };

  const handleMoveAllToStage = () => {
    // Move all selected performers to stage, respecting replacement logic
    selectedPerformers.forEach((performer) => {
      movePerformerToStage(performer);
    });
  };

  const handleRemoveFromSelection = (performer: Performer) => {
    // If this performer was replacing someone, clear the replacement info from that person
    if (performer.replacingPerformerId) {
      const replacedPerformer = performers.find(
        (p) => p.id === performer.replacingPerformerId,
      );
      if (
        replacedPerformer &&
        replacedPerformer.status === 'pending_removal' &&
        replacedPerformer.replacementName === performer.name
      ) {
        updatePerformerMutation.mutate({
          id: performer.replacingPerformerId,
          updates: {
            status: 'on_stage',
            replacementName: null,
          },
        });
      }
    }

    // Remove performer from selection
    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'participant',
        currentInstruments: [],
        replacingPerformerId: null,
      },
    });
  };

  // Don't render if no performers are selected
  if (selectedPerformers.length === 0) {
    return null;
  }

  const getReplacingPerformerName = (replacingId: number | null) => {
    if (!replacingId) return null;
    const performer = performers.find((p) => p.id === replacingId);
    return performer?.name || null;
  };

  return (
    <div className="bg-blue-100/30 backdrop-blur-xl rounded-2xl shadow-2xl border border-blue-200/40 overflow-hidden mb-2 relative group"
      style={{
        background: 'linear-gradient(135deg, rgba(59,130,246,0.25) 0%, rgba(59,130,246,0.1) 100%)',
        boxShadow: '0 8px 32px rgba(59,130,246,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}>
      {/* Liquid Glass Specular Highlight */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />
      
      <div className="px-6 py-4 border-b border-blue-200/40 relative z-10">
        {/* Desktop layout: side by side */}
        <div className="hidden lg:flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-blue-900/90 flex items-center drop-shadow-sm">
              <UserCheck className="h-5 w-5 mr-2 text-blue-600/90 drop-shadow-sm" />
              Selected
              <span className="ml-2 text-sm font-normal text-blue-600/80 drop-shadow-sm">
                ({selectedPerformers.length})
              </span>
            </h2>
          </div>
          {selectedPerformers.length > 1 && (
            <Button
              size="sm"
              onClick={handleMoveAllToStage}
              disabled={disabled || updatePerformerMutation.isPending}
              className="bg-blue-600/80 backdrop-blur-sm hover:bg-blue-700/90 text-white border border-blue-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed h-7 px-3 text-xs"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <ArrowUp className="h-3 w-3 mr-1" />
              Put All ({selectedPerformers.length})
            </Button>
          )}
        </div>

        {/* Mobile layout: stacked */}
        <div className="block lg:hidden">
          <div>
            <h2 className="text-lg font-semibold text-blue-900/90 flex items-center drop-shadow-sm">
              <UserCheck className="h-5 w-5 mr-2 text-blue-600/90 drop-shadow-sm" />
                Selected
              <span className="ml-2 text-sm font-normal text-blue-600/80 drop-shadow-sm">
                ({selectedPerformers.length})
              </span>
            </h2>
          </div>
          {selectedPerformers.length > 1 && (
            <Button
              onClick={handleMoveAllToStage}
              disabled={disabled || updatePerformerMutation.isPending}
              className="bg-blue-600/80 backdrop-blur-sm hover:bg-blue-700/90 text-white w-full mt-3 border border-blue-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <ArrowUp className="h-4 w-4 mr-2" />
              Put All ({selectedPerformers.length})
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className={forceMobileView ? "hidden" : "hidden lg:block overflow-x-auto relative z-10"}>
        <table className="min-w-full divide-y divide-blue-200/30">
          <thead className="bg-blue-100/40 backdrop-blur-sm">
            <tr>
              <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-blue-700/80 uppercase tracking-wider drop-shadow-sm">
                Name
              </th>
              <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-blue-700/80 uppercase tracking-wider drop-shadow-sm">
                Replacing
              </th>
              <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-blue-700/80 uppercase tracking-wider drop-shadow-sm">
                Instrument
              </th>
              <th className="w-1/4 px-6 py-3 text-right text-xs font-medium text-blue-700/80 uppercase tracking-wider drop-shadow-sm">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/30 backdrop-blur-sm divide-y divide-blue-200/30">
            {selectedPerformers.map((performer) => {
              const replacingName = getReplacingPerformerName(
                performer.replacingPerformerId,
              );
              return (
                <tr key={performer.id} className="hover:bg-white/50 backdrop-blur-sm transition-all duration-300">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
                        {performer.name}
                      </div>

                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {replacingName ? (
                      <ReplacementInfo 
                        mode="replacing" 
                        name={replacingName}
                        instruments={(() => {
                          const replacedPerformer = performers.find(p => p.id === performer.replacingPerformerId);
                          return replacedPerformer?.currentInstruments || null;
                        })()}
                      />
                    ) : (
                      <span className="text-sm text-slate-500/80 drop-shadow-sm">
                        No replacement
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {performer.instruments?.length ? (
                        performer.instruments.length === 1 ? (
                          // Single instrument - show as chip
                          <Badge 
                            variant="outline" 
                            className="bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50"
                            style={{
                              boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                            }}
                          >
                            {performer.instruments[0]}
                          </Badge>
                        ) : (
                          // Multiple instruments - show dropdown
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled={disabled || updatePerformerMutation.isPending}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-auto px-2 py-1 bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50 hover:bg-blue-200/60 transition-all duration-200"
                                style={{
                                  boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                                }}
                              >
                                <span className="text-sm font-medium">
                                  {performer.currentInstruments?.length ? performer.currentInstruments[0] : 'Select Instrument'}
                                </span>
                                <ChevronDown className="ml-1 h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-48">
                              <DropdownMenuRadioGroup 
                                value={(performer.currentInstruments || [])[0] || ""} 
                                onValueChange={(value) => handleInstrumentChange(performer.id, value)}
                              >
                                {performer.instruments.map((instrument) => (
                                  <DropdownMenuRadioItem key={instrument} value={instrument}>
                                    {instrument}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      ) : (
                        <span className="text-sm text-slate-500/80 italic drop-shadow-sm">
                          No instruments registered
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2 justify-end">
                      <Button
                        size="sm"
                        onClick={() => handleMoveToStage(performer)}
                        disabled={disabled || updatePerformerMutation.isPending}
                        className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                        }}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        On Stage
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveFromSelection(performer)}
                        disabled={disabled || updatePerformerMutation.isPending}
                        className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                        style={{
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className={forceMobileView ? "block divide-y divide-blue-200/30 relative z-10" : "block lg:hidden divide-y divide-blue-200/30 relative z-10"}>
        {selectedPerformers.map((performer) => {
          const replacingName = getReplacingPerformerName(
            performer.replacingPerformerId,
          );
          return (
            <div key={performer.id} className="p-4 bg-white/30 backdrop-blur-sm transition-all duration-300">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
                    {performer.name}
                  </h3>
                  
                  {/* Instrument selector below name */}
                  {performer.instruments?.length ? (
                    <div className="mt-2">
                      {performer.instruments.length === 1 ? (
                        // Single instrument - show as chip
                        <Badge 
                          variant="outline" 
                          className="inline-block bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50"
                          style={{
                            boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }}
                        >
                          {performer.instruments[0]}
                        </Badge>
                      ) : (
                        // Multiple instruments - show dropdown
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild disabled={disabled || updatePerformerMutation.isPending}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto px-2 py-1 bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50 hover:bg-blue-200/60 transition-all duration-200"
                              style={{
                                boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
                              }}
                            >
                              <span className="text-xs font-medium">
                                {performer.currentInstruments?.length ? performer.currentInstruments[0] : 'Select Instrument'}
                              </span>
                              <ChevronDown className="ml-1 h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-48">
                            <DropdownMenuRadioGroup 
                              value={(performer.currentInstruments || [])[0] || ""} 
                              onValueChange={(value) => handleInstrumentChange(performer.id, value)}
                            >
                              {performer.instruments.map((instrument) => (
                                <DropdownMenuRadioItem key={instrument} value={instrument}>
                                  {instrument}
                                </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    onClick={() => handleMoveToStage(performer)}
                    disabled={disabled || updatePerformerMutation.isPending}
                    className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    On Stage
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveFromSelection(performer)}
                    disabled={disabled || updatePerformerMutation.isPending}
                    className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              {replacingName && (
                <ReplacementInfo 
                  mode="replacing" 
                  name={replacingName}
                  instruments={(() => {
                    const replacedPerformer = performers.find(p => p.id === performer.replacingPerformerId);
                    return replacedPerformer?.currentInstruments || null;
                  })()}
                  className="mt-2"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
